/*
 * Tooltip.js — Nombre visible de los botones de solo ícono y de los círculos de estado (C-022).
 *
 * Un solo tooltip para toda la app: cualquier elemento con `data-tooltip="…"` lo muestra.
 *   - Con el mouse aparece tras 300 ms; con el teclado (foco visible), de inmediato.
 *   - Escape lo cierra (y no cierra el modal de abajo); no tapa el elemento; si el mouse pasa
 *     del elemento al tooltip, no desaparece (WCAG 1.4.13).
 *   - Se pone arriba del elemento, o abajo si no cabe.
 * `BotonIcono` arma el botón con su `aria-label`, el mismo texto en `data-tooltip` y el ícono oculto
 * para lectores de pantalla, para que nadie tenga que acordarse en cada botón.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const RETRASO_MOUSE = 300;
const RETRASO_SALIDA = 120;
const SEPARACION = 6;
const MARGEN = 4;

const conTooltip = (nodo) =>
  nodo && nodo.closest ? nodo.closest('[data-tooltip]') : null;

const tieneFocoVisible = (el) => {
  try { return el.matches(':focus-visible'); } catch (e) { return true; }
};

export function TooltipGlobal() {
  const [actual, setActual] = useState(null); // { el, texto }
  const [pos, setPos] = useState(null);
  const caja = useRef(null);
  const timer = useRef(null);
  const actualRef = useRef(null);
  actualRef.current = actual;

  useEffect(() => {
    const limpiar = () => { clearTimeout(timer.current); timer.current = null; };
    const mostrar = (el) => {
      limpiar();
      const texto = el.getAttribute('data-tooltip');
      if (!texto) return;
      setPos(null);
      setActual({ el, texto });
    };
    const esconder = () => { limpiar(); setActual(null); };
    const esconderLuego = () => { limpiar(); timer.current = setTimeout(esconder, RETRASO_SALIDA); };
    const dentroDelTooltip = (nodo) => caja.current && nodo && caja.current.contains(nodo);

    const alEntrar = (e) => {
      if (dentroDelTooltip(e.target)) { limpiar(); return; }
      const el = conTooltip(e.target);
      if (!el) return;
      if (actualRef.current && actualRef.current.el === el) { limpiar(); return; }
      limpiar();
      timer.current = setTimeout(() => mostrar(el), actualRef.current ? 0 : RETRASO_MOUSE);
    };
    const alSalir = (e) => {
      const desde = conTooltip(e.target) || (dentroDelTooltip(e.target) ? actualRef.current?.el : null);
      if (!desde) return;
      const hacia = e.relatedTarget;
      if (hacia && (desde.contains(hacia) || dentroDelTooltip(hacia))) return;
      if (actualRef.current) esconderLuego(); else limpiar();
    };
    const alEnfocar = (e) => {
      const el = conTooltip(e.target);
      if (el && el === e.target && tieneFocoVisible(el)) mostrar(el);
    };
    const alDesenfocar = (e) => {
      if (actualRef.current && actualRef.current.el === e.target) esconder();
    };
    const alTeclear = (e) => {
      if (e.key === 'Escape' && actualRef.current) {
        e.stopPropagation();
        e.preventDefault();
        esconder();
      }
    };
    const alPulsar = (e) => {
      if (!dentroDelTooltip(e.target)) esconder();
    };

    document.addEventListener('mouseover', alEntrar);
    document.addEventListener('mouseout', alSalir);
    document.addEventListener('focusin', alEnfocar);
    document.addEventListener('focusout', alDesenfocar);
    document.addEventListener('keydown', alTeclear, true);
    document.addEventListener('pointerdown', alPulsar, true);
    window.addEventListener('scroll', esconder, true);
    window.addEventListener('resize', esconder);
    return () => {
      limpiar();
      document.removeEventListener('mouseover', alEntrar);
      document.removeEventListener('mouseout', alSalir);
      document.removeEventListener('focusin', alEnfocar);
      document.removeEventListener('focusout', alDesenfocar);
      document.removeEventListener('keydown', alTeclear, true);
      document.removeEventListener('pointerdown', alPulsar, true);
      window.removeEventListener('scroll', esconder, true);
      window.removeEventListener('resize', esconder);
    };
  }, []);

  // Si el elemento desaparece (se cerró el modal), el tooltip también.
  useEffect(() => {
    if (!actual) return undefined;
    const revisar = setInterval(() => {
      if (!document.body.contains(actual.el)) setActual(null);
    }, 300);
    return () => clearInterval(revisar);
  }, [actual]);

  useLayoutEffect(() => {
    if (!actual || !caja.current) return;
    const r = actual.el.getBoundingClientRect();
    const t = caja.current.getBoundingClientRect();
    const ancho = window.innerWidth;
    let top = r.top - t.height - SEPARACION;
    let lado = 'arriba';
    if (top < MARGEN) { top = r.bottom + SEPARACION; lado = 'abajo'; }
    let left = r.left + r.width / 2 - t.width / 2;
    left = Math.max(MARGEN, Math.min(left, ancho - t.width - MARGEN));
    setPos({ top, left, lado });
  }, [actual]);

  if (!actual) return null;
  return createPortal(
    <div
      ref={caja}
      role="tooltip"
      className={`tooltip-global${pos ? ` visible ${pos.lado}` : ''}`}
      style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0 }}
    >
      {actual.texto}
    </div>,
    document.body
  );
}

/** Botón de solo ícono: `etiqueta` es su nombre accesible y su tooltip. */
export function BotonIcono({ etiqueta, children, type = 'button', ...props }) {
  return (
    <button type={type} aria-label={etiqueta} data-tooltip={etiqueta} {...props}>
      {children}
    </button>
  );
}
